import { useState, useRef, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Brain, Trophy, Play } from "lucide-react";
import { FRIENDS } from "../../data/site";

const GAME_H = 440;
const PLAYER_W = 88;
const ITEM = 54;
const DURATION = 30;

// Easter egg: every 5th drop is one of our providers' photos.
const PROVIDERS = ["dora.jpg", "team-ryan.jpg", "team-shannon.jpg", "team-tessa.jpg", "team-jessica.jpg", "dora-fun.jpg"];

export default function NeuroGame() {
    const areaRef = useRef(null);
    const [status, setStatus] = useState("idle"); // idle | playing | over
    const [score, setScore] = useState(0);
    const [best, setBest] = useState(0);
    const [time, setTime] = useState(DURATION);
    const [items, setItems] = useState([]);
    const [playerX, setPlayerX] = useState(0.5); // fraction 0..1

    // refs mirror state for the animation loop
    const statusRef = useRef(status);
    const playerXRef = useRef(playerX);
    const itemsRef = useRef([]);
    const scoreRef = useRef(0);
    const rafRef = useRef(null);
    const lastSpawn = useRef(0);
    const startTime = useRef(0);
    const idRef = useRef(0);
    const spawnCountRef = useRef(0);

    useEffect(() => {
        statusRef.current = status;
    }, [status]);
    useEffect(() => {
        playerXRef.current = playerX;
    }, [playerX]);

    useEffect(() => {
        const stored = Number(localStorage.getItem("neuroBest") || 0);
        setBest(stored);
    }, []);

    const width = useCallback(() => areaRef.current?.clientWidth || 800, []);

    const endGame = useCallback(() => {
        setStatus("over");
        statusRef.current = "over";
        cancelAnimationFrame(rafRef.current);
        setBest((b) => {
            const nb = Math.max(b, scoreRef.current);
            localStorage.setItem("neuroBest", String(nb));
            return nb;
        });
    }, []);

    const loop = useCallback(
        (ts) => {
            if (statusRef.current !== "playing") return;
            const w = width();
            const elapsed = (ts - startTime.current) / 1000;
            const remaining = Math.max(0, DURATION - elapsed);
            setTime(Math.ceil(remaining));
            if (remaining <= 0) {
                endGame();
                return;
            }

            // spawn
            const spawnGap = 620 - Math.min(300, elapsed * 12);
            if (ts - lastSpawn.current > spawnGap) {
                lastSpawn.current = ts;
                spawnCountRef.current += 1;
                const isProvider = spawnCountRef.current % 5 === 0;
                const f = FRIENDS[Math.floor(Math.random() * FRIENDS.length)];
                idRef.current += 1;
                itemsRef.current.push({
                    id: idRef.current,
                    x: 20 + Math.random() * (w - 40 - ITEM),
                    y: -ITEM,
                    vy: 2.4 + Math.random() * 2.2 + elapsed * 0.06,
                    sprite: isProvider
                        ? PROVIDERS[Math.floor(Math.random() * PROVIDERS.length)]
                        : f.sprite,
                    color: isProvider ? "#fbbf24" : f.color,
                    provider: isProvider,
                });
            }

            const playerLeft = playerXRef.current * (w - PLAYER_W);
            const catchY = GAME_H - 96;
            const next = [];
            for (const it of itemsRef.current) {
                it.y += it.vy;
                const caught =
                    it.y + ITEM >= catchY &&
                    it.y <= GAME_H - 20 &&
                    it.x + ITEM > playerLeft &&
                    it.x < playerLeft + PLAYER_W;
                if (caught) {
                    scoreRef.current += 1;
                    setScore(scoreRef.current);
                    continue;
                }
                if (it.y > GAME_H + ITEM) continue; // missed, drift away
                next.push(it);
            }
            itemsRef.current = next;
            setItems([...next]);

            rafRef.current = requestAnimationFrame(loop);
        },
        [endGame, width]
    );

    const start = useCallback(() => {
        scoreRef.current = 0;
        itemsRef.current = [];
        idRef.current = 0;
        spawnCountRef.current = 0;
        setScore(0);
        setItems([]);
        setTime(DURATION);
        setPlayerX(0.5);
        playerXRef.current = 0.5;
        setStatus("playing");
        statusRef.current = "playing";
        startTime.current = performance.now();
        lastSpawn.current = performance.now();
        rafRef.current = requestAnimationFrame(loop);
    }, [loop]);

    useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

    const handlePointer = (e) => {
        if (statusRef.current !== "playing" || !areaRef.current) return;
        const rect = areaRef.current.getBoundingClientRect();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const frac = (clientX - rect.left) / rect.width;
        setPlayerX(Math.min(1, Math.max(0, frac)));
    };

    useEffect(() => {
        const onKey = (e) => {
            if (statusRef.current !== "playing") return;
            if (e.key === "ArrowLeft")
                setPlayerX((x) => Math.max(0, x - 0.06));
            if (e.key === "ArrowRight")
                setPlayerX((x) => Math.min(1, x + 0.06));
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    return (
        <section
            id="neuro-section"
            data-testid="neuro-section"
            className="relative scroll-mt-24 py-24 sm:py-32"
            style={{
                background:
                    "linear-gradient(rgb(19,14,10) 0%, rgb(23,17,38) 18%, rgb(23,17,38) 82%, rgb(19,14,10) 100%)",
            }}
        >
            <div className="mx-auto w-full max-w-6xl px-6">
                <motion.div
                    initial={{ opacity: 0, y: 36 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="flex flex-col items-start gap-8 sm:flex-row sm:items-center"
                >
                    <div className="min-w-0 flex-1">
                        <p className="flex items-center gap-2 text-xs uppercase tracking-[0.35em] text-sky-300/80">
                            <Brain className="h-4 w-4" />
                            A brighter corner of the waiting room
                        </p>
                        <h2 className="mt-5 font-serif text-2xl tracking-tight text-white sm:text-3xl lg:text-4xl">
                            Neuro & the 'Ception Force Friends
                        </h2>
                        <p className="mt-4 max-w-2xl text-base leading-relaxed text-white/65">
                            Meet Professor Neuro and his eight sensory friends —
                            the real crew from Dora's official artwork. Help the
                            Professor catch their good signals before they drift
                            away. Thirty seconds, quick hits, zero wrong answers.
                        </p>
                    </div>
                    <img
                        alt="Official poster art for Neuro and the 'Ception Force Friends"
                        src="/images/neuro-poster.jpg"
                        className="w-40 shrink-0 rotate-2 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.55)] ring-1 ring-white/20 transition-transform duration-500 hover:rotate-0 sm:w-52"
                    />
                </motion.div>

                <ul className="mt-8 flex flex-wrap gap-2.5">
                    {FRIENDS.map((f) => (
                        <li
                            key={f.name}
                            title={f.title}
                            className="flex items-center gap-2 rounded-full border border-white/15 bg-white/5 py-1.5 pl-1.5 pr-3.5 text-xs text-white/80 backdrop-blur-sm"
                        >
                            <img
                                alt=""
                                aria-hidden="true"
                                src={`/images/${f.sprite}`}
                                className="h-6 w-6 rounded-full object-cover"
                                style={{ border: `1.5px solid ${f.color}` }}
                            />
                            {f.name}
                            <span className="text-white/40">· {f.tag}</span>
                        </li>
                    ))}
                </ul>

                <div className="mt-10">
                    <div
                        ref={areaRef}
                        data-testid="game-area"
                        onMouseMove={handlePointer}
                        onTouchMove={handlePointer}
                        className="relative select-none overflow-hidden rounded-3xl border border-white/12 shadow-[0_30px_90px_rgba(0,0,0,0.5)]"
                        style={{
                            height: GAME_H,
                            background:
                                "radial-gradient(at 50% 0%, rgb(36,26,61) 0%, rgb(20,16,31) 60%, rgb(13,10,20) 100%)",
                            cursor: status === "playing" ? "none" : "default",
                        }}
                    >
                        {/* stars */}
                        <div
                            aria-hidden="true"
                            className="absolute inset-0 opacity-50"
                            style={{
                                backgroundImage:
                                    "radial-gradient(1.5px 1.5px at 12% 22%, rgba(255,255,255,0.7) 50%, transparent 51%), radial-gradient(1.5px 1.5px at 68% 12%, rgba(255,255,255,0.5) 50%, transparent 51%), radial-gradient(2px 2px at 42% 34%, rgba(255,255,255,0.4) 50%, transparent 51%), radial-gradient(1.5px 1.5px at 88% 30%, rgba(255,255,255,0.6) 50%, transparent 51%), radial-gradient(1.5px 1.5px at 28% 48%, rgba(255,255,255,0.4) 50%, transparent 51%)",
                            }}
                        />

                        {/* HUD */}
                        <div className="absolute inset-x-0 top-0 z-30 flex items-center justify-between px-5 py-4">
                            <span
                                data-testid="game-score"
                                className="font-serif text-3xl text-amber-300"
                            >
                                {score}
                            </span>
                            <div className="flex items-center gap-3 text-sm text-white/70">
                                <span
                                    data-testid="game-best"
                                    className="flex items-center gap-1.5"
                                >
                                    <Trophy className="h-4 w-4 text-amber-400" />
                                    {best}
                                </span>
                                <span
                                    data-testid="game-timer"
                                    className="rounded-full bg-white/10 px-3 py-1 font-mono text-sm text-white/80 ring-1 ring-white/15"
                                >
                                    {time}s
                                </span>
                            </div>
                        </div>

                        {/* falling friends */}
                        {items.map((it) => (
                            <img
                                key={it.id}
                                src={`/images/${it.sprite}`}
                                alt=""
                                draggable="false"
                                className="absolute rounded-full object-cover"
                                style={{
                                    width: ITEM,
                                    height: ITEM,
                                    left: it.x,
                                    top: it.y,
                                    border: it.provider ? `3px solid ${it.color}` : `2px solid ${it.color}`,
                                    boxShadow: it.provider
                                        ? `0 0 26px ${it.color}, 0 0 8px #fff`
                                        : `0 0 16px ${it.color}88`,
                                }}
                            />
                        ))}

                        {/* player */}
                        <div
                            data-testid="game-player"
                            className="absolute bottom-3 z-20"
                            style={{
                                width: PLAYER_W,
                                height: PLAYER_W,
                                left: `calc(${playerX} * (100% - ${PLAYER_W}px))`,
                                transition: "left 90ms linear",
                            }}
                        >
                            <img
                                src="/images/sprite-neuro.png"
                                alt="Professor Neuro"
                                draggable="false"
                                className="h-full w-full rounded-full object-cover ring-2 ring-sky-300/50 drop-shadow-[0_0_20px_rgba(56,189,248,0.55)]"
                            />
                        </div>

                        {/* overlay */}
                        {status !== "playing" && (
                            <div className="absolute inset-0 z-40 flex flex-col items-center justify-center gap-5 bg-[#0d0a14]/80 px-6 text-center backdrop-blur-sm">
                                <div className="h-24 w-24">
                                    <img
                                        src="/images/sprite-neuro.png"
                                        alt="Professor Neuro"
                                        className="h-full w-full rounded-full object-cover ring-2 ring-sky-300/50 drop-shadow-[0_0_20px_rgba(56,189,248,0.55)]"
                                    />
                                </div>
                                {status === "over" ? (
                                    <>
                                        <p
                                            data-testid="game-over"
                                            className="max-w-md font-serif text-2xl text-white/90 sm:text-3xl"
                                        >
                                            Great catching! You gathered {score}{" "}
                                            good signal{score === 1 ? "" : "s"}.
                                        </p>
                                        <p className="text-sm text-white/60">
                                            Best today: {best}
                                        </p>
                                    </>
                                ) : (
                                    <p
                                        data-testid="game-intro"
                                        className="max-w-md font-serif text-xl text-white/90 sm:text-2xl"
                                    >
                                        Catch the friends' good signals. Move
                                        with your mouse, finger, or arrow keys.
                                    </p>
                                )}
                                <button
                                    data-testid="game-start-button"
                                    onClick={start}
                                    className="inline-flex items-center gap-2 rounded-full bg-amber-400 px-7 py-3.5 text-sm font-bold text-[#1F1510] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-amber-300"
                                >
                                    <Play className="h-4 w-4" />
                                    {status === "over"
                                        ? "Play again"
                                        : "Start — 30 seconds"}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}
